import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { AskDepth, AskQuote, AskScope, AskSource } from "@mosaic/shared";
import { api, askStream, type AskPreflight, type AskStoredMessage } from "../api/client.js";
import { askScopeKey } from "../lib/askScope.js";
import { filingHref } from "../lib/citations.js";
import { scaleDecimal } from "../lib/format.js";

/**
 * Ask: questions against the filings, answered with citations.
 *
 * **The control the owner is given is the depth, because that is where the cost is.**
 * Depth moves the bill far more than the model tier does, so a model picker with the depth
 * hidden behind it would let the larger number move unwatched.
 *
 * The model is still choosable, defaulting from the depth, because latency and judgement
 * are the owner's to weigh.
 */

const DEPTHS: Array<{ key: AskDepth; label: string; blurb: string }> = [
  {
    key: "navigate",
    label: "Navigate",
    blurb:
      "Opens the outline, finds phrases literally, reads the sections it picks. " +
      "Answers here, in seconds.",
  },
  {
    key: "whole",
    label: "Whole document",
    blurb:
      "Loads the filing entire. The only mode that can settle a question needing parts " +
      "of the document nowhere near each other — whose debt a figure is, say. Queued to " +
      "a Claude Code session, so the answer arrives in minutes rather than seconds.",
  },
];

/**
 * The models offered beside the server's default, most capable last. The default itself
 * comes from the preflight, so it matches what would run.
 */
const MODELS = [
  { id: "claude-haiku-4-5-20251001", label: "Haiku 4.5 — fastest" },
  { id: "claude-sonnet-5-5", label: "Sonnet 5.5" },
  { id: "claude-opus-5", label: "Opus 5 — most careful" },
];

/**
 * The model options, always including the server's default. A `<select>` whose value
 * matches no option shows its first option, so a default missing from the list would show
 * one model while the question ran on another.
 *
 * @param serverDefault The model the server would run, from the preflight.
 * @returns `MODELS`, plus the default when it is not among them.
 */
export function modelOptions(serverDefault: string | null | undefined) {
  if (!serverDefault || MODELS.some((m) => m.id === serverDefault)) return MODELS;
  return [...MODELS, { id: serverDefault, label: serverDefault }];
}

interface Turn {
  role: "user" | "assistant";
  body: string;
  sources: AskSource[];
  quotes: AskQuote[];
  entityWarning: string | null;
  usd: string | null;
  model: string | null;
  steps: Array<{ tool: string; detail: string }>;
  strategy: string | null;
}

/**
 * The Ask panel for one subject. The shell derives the scope (`lib/askScope.ts`); the
 * panel neither derives it nor offers a picker, so the page and the question cannot
 * disagree about what is being asked about.
 */
export function Ask({ scope }: { scope: AskScope }) {
  const [question, setQuestion] = useState("");
  const [depth, setDepth] = useState<AskDepth>("navigate");
  const [modePicker, setModePicker] = useState(false);
  /**
   * Set while a queued answer is outstanding. `no_executor` is kept apart from `waiting`
   * because one asks for patience and the other is a deployment that can never answer.
   */
  const [queued, setQueued] = useState<"waiting" | "no_executor" | null>(null);
  const [model, setModel] = useState<string>("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [threadId, setThreadId] = useState<string | undefined>();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const preflight = useQuery({
    queryKey: ["askPreflight", depth, askScopeKey(scope)],
    queryFn: () => api.askPreflight(depth, scope),
  });

  async function submit() {
    const text = question.trim();
    if (!text || running) return;

    setRunning(true);
    setError(null);
    setQuestion("");
    setTurns((t) => [
      ...t,
      { role: "user", body: text, sources: [], quotes: [], entityWarning: null, usd: null, model: null, steps: [], strategy: null },
      { role: "assistant", body: "", sources: [], quotes: [], entityWarning: null, usd: null, model: null, steps: [], strategy: null },
    ]);

    // Replaces the last turn on every event: a streamed answer is one object growing, not a
    // list of appended fragments.
    const patch = (fn: (turn: Turn) => Turn) =>
      setTurns((all) => all.map((t, i) => (i === all.length - 1 ? fn(t) : t)));

    abort.current = new AbortController();
    try {
      await askStream(
        {
          question: text,
          scope,
          depth,
          ...(model ? { model } : {}),
          ...(threadId ? { threadId } : {}),
        },
        {
          context: (c) => {
            setThreadId(c.threadId);
            patch((t) => ({ ...t, model: c.model, strategy: c.strategy }));
          },
          step: (s) => patch((t) => ({ ...t, steps: [...t.steps, s] })),
          delta: (d) => patch((t) => ({ ...t, body: t.body + d.text })),
          citation: (q) => patch((t) => ({ ...t, quotes: [...t.quotes, q] })),
          entity_warning: (w) => patch((t) => ({ ...t, entityWarning: w.message })),
          done: (d) =>
            patch((t) => ({
              ...t,
              sources: d.sources,
              model: d.model,
              usd: d.usage.estimatedUsd,
            })),
          /**
           * A `whole`-depth question is queued for a Claude Code session. With no agent token
           * configured it can never be claimed, which looks exactly like a slow one, so both states
           * are said rather than left to a turn that never fills in.
           */
          queued: (q) => {
            setThreadId(q.threadId);
            setQueued(q.agentConfigured ? "waiting" : "no_executor");
            patch((t) => ({
              ...t,
              strategy: "whole_document",
              body: q.agentConfigured
                ? "Queued for a Claude Code session. The answer will appear here when it " +
                  "lands, and Mattermost will tell you."
                : "Queued, but no agent token is configured on this deployment, so nothing " +
                  "can claim it. Set MOSAIC_AGENT_TOKEN and ask again.",
            }));
          },
          error: (e) => setError(e.message),
        },
        abort.current.signal,
      );
    } catch (cause) {
      if ((cause as Error).name !== "AbortError") setError((cause as Error).message);
    } finally {
      setRunning(false);
      abort.current = null;
      // The cost view on the Operations page reads the same ledger.
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    }
  }

  /**
   * Polls only while a queued answer is outstanding, and stops when it lands: an open page
   * on a finished thread must not poll for an answer that cannot change.
   */
  const pending = useQuery({
    queryKey: ["askThread", threadId, queued],
    queryFn: () => api.askThread(threadId!),
    enabled: queued === "waiting" && threadId !== null,
    refetchInterval: 5_000,
  });

  useEffect(() => {
    if (queued !== "waiting") return;
    const answer = [...(pending.data?.items ?? [])]
      .reverse()
      .find((m) => m.role === "assistant");
    if (!answer) return;
    setQueued(null);
    setTurns((all) =>
      all.map((t, i) =>
        i === all.length - 1
          ? {
              ...t,
              body: answer.body,
              quotes: answer.quotes,
              sources: answer.sources,
              model: answer.model,
              strategy: "whole_document",
            }
          : t,
      ),
    );
  }, [pending.data, queued]);

  return (
    <>
      {/*
        One pill carrying the price beside the mode: depth is where the cost moves, so
        the number stays in view.
      */}
      <div className="ask-controls">
        <button
          className={`ask-pill${modePicker ? " on" : ""}`}
          aria-expanded={modePicker}
          aria-label="Reading mode"
          onClick={() => setModePicker(!modePicker)}
        >
          <b>{DEPTHS.find((d) => d.key === depth)?.label}</b>
          <span className="ask-pill-cost">{priceLabel(preflight.data)}</span>
          <span className="ask-pill-caret">▾</span>
        </button>

        <select
          className="ask-model"
          aria-label="Model"
          value={model || preflight.data?.model || MODELS[1]!.id}
          onChange={(e) => setModel(e.target.value)}
        >
          {modelOptions(preflight.data?.model).map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {modePicker && (
        <div className="ask-sheet" role="listbox" aria-label="Reading mode">
          {DEPTHS.map((d) => (
            <button
              key={d.key}
              role="option"
              aria-selected={depth === d.key}
              className={`ask-mode${depth === d.key ? " on" : ""}`}
              onClick={() => {
                setDepth(d.key);
                setModePicker(false);
              }}
            >
              <span className="ask-mode-label">{d.label}</span>
              <span className="ask-mode-blurb">{d.blurb}</span>
              {/*
                Each mode names its executor, the consequence the choice has: one streams
                from the API key and answers while you watch, the other is queued to a
                session on the subscription.
              */}
              <span className="ask-mode-run">
                {d.key === "whole"
                  ? preflight.data?.largestDocument
                    ? `queued · ${preflight.data.largestDocument.charCount.toLocaleString()} chars whole`
                    : "queued to a Claude Code session"
                  : "answers here · API"}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="ask-turns">
        {turns.map((turn, i) => (
          <TurnView key={i} turn={turn} running={running && i === turns.length - 1} />
        ))}
      </div>

      {error && (
        <div className="notice">
          <b>That question did not go through</b>
          {error}
        </div>
      )}

      <div className="ask-compose">
        <textarea
          aria-label="Question"
          placeholder="Why does management say they are separating?"
          value={question}
          rows={2}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void submit();
          }}
        />
        <div className="ask-actions">
          <span className="hint">⌘↵ to ask</span>
          {running ? (
            <button className="iconbtn" onClick={() => abort.current?.abort()}>
              Stop
            </button>
          ) : (
            <button className="iconbtn" onClick={() => void submit()} disabled={!question.trim()}>
              Ask
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * One turn of an Ask thread: the owner's question, or an answer with the steps taken, its
 * quotes marked verified or not, any entity warning, and the passages read.
 *
 * @internal
 * @param running Shows a caret after the body while the answer is still streaming.
 */
function TurnView({ turn, running }: { turn: Turn; running: boolean }) {
  if (turn.role === "user") return <div className="ask-q">{turn.body}</div>;

  return (
    <div className="ask-a">
      {/*
        What it is reading, as it reads it. A navigated answer makes tool calls before
        the first word of prose, and a blank pane during that looks like a stall.
      */}
      {turn.steps.length > 0 && (
        <div className="ask-steps">
          {turn.steps.map((s, i) => (
            <span key={i} className="ask-step">
              <b>{s.tool}</b> {s.detail}
            </span>
          ))}
        </div>
      )}

      <div className="ask-body">
        {turn.body}
        {running && <span className="ask-caret" />}
      </div>

      {/*
        The failure quote verification cannot catch: a real, correctly located quote
        about a different entity than the answer attributes it to.
      */}
      {turn.entityWarning && (
        <div className="notice">
          <b>Check whose figure this is</b>
          {turn.entityWarning}
        </div>
      )}

      {turn.quotes.length > 0 && (
        <div className="ask-quotes">
          {turn.quotes.map((q, i) => (
            <div key={i} className={`ask-quote${q.verified ? "" : " unverified"}`}>
              <blockquote>{q.text}</blockquote>
              {q.verified && q.accession ? (
                <Link
                  to={filingHref(q.accession, {
                    documentId: q.documentId,
                    charStart: q.charStart,
                    charEnd: q.charEnd,
                  })}
                  className="ask-cite"
                >
                  verified in {q.accession} @{q.charStart}
                </Link>
              ) : (
                // Marked rather than deleted, unlike extraction: dropping it would leave the claim
                // standing with no citation, which reads as less suspicious than it is. The wording
                // admits the innocent case of quotation marks used for emphasis.
                <span className="ask-cite">
                  not found in anything that was read — the model’s own words, or a
                  quotation that does not match the filing
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {turn.sources.length > 0 && (
        <details className="ask-sources">
          <summary>
            {turn.sources.length} passage{turn.sources.length === 1 ? "" : "s"} read
            {turn.strategy === "whole_document" ? " · whole document in context" : ""}
            {turn.usd !== null ? ` · $${scaleDecimal(turn.usd, 0, 4)}` : ""}
            {turn.model ? ` · ${turn.model}` : ""}
          </summary>
          {turn.sources.map((s, i) => (
            <div key={i} className="ask-source">
              <Link to={`/filings/${s.accession}`}>{s.filename}</Link>
              {s.section ? ` · ${s.section}` : ""} · {s.companyName ?? "unknown filer"} ·{" "}
              {s.charStart}–{s.charEnd} · via {s.via}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

/** Renders a stored thread, for reopening a past question. */
export function StoredTurns({ items }: { items: AskStoredMessage[] }) {
  return (
    <div className="ask-turns">
      {items.map((m) => (
        <TurnView
          key={m.id}
          running={false}
          turn={{
            role: m.role,
            body: m.body,
            sources: m.sources,
            quotes: m.quotes,
            entityWarning: m.entityWarning,
            usd: m.usage?.estimatedUsd ?? null,
            model: m.model,
            steps: [],
            strategy: null,
          }}
        />
      ))}
    </div>
  );
}

/**
 * The price beside the mode, or an honest absence.
 *
 * Guarded because this sits inside the reading surface: a preflight without a price must
 * degrade to a missing figure rather than throw and take the situation page down.
 *
 * `whole` quotes a size rather than a price: it is queued to a Claude Code session on the
 * subscription rather than billed, so a dollar figure there would be wrong.
 *
 * @internal
 */
function priceLabel(preflight: AskPreflight | undefined): string {
  if (!preflight) return "";
  if (preflight.largestDocument && !preflight.isCeiling) {
    return `${Math.round(preflight.largestDocument.charCount / 1000)}k chars`;
  }
  if (typeof preflight.estimatedUsd !== "string") return "cost unknown";
  const usd = preflight.estimatedUsd;
  // Under ten cents gets a third place, so a small cost does not read as $0.00.
  const decimals = /^0\.0/.test(usd) ? 3 : 2;
  return `${preflight.isCeiling ? "≤" : ""}$${scaleDecimal(usd, 0, decimals)}`;
}
